import { Controller } from "@hotwired/stimulus"

const MOBILE_BREAKPOINT = 768

export default class extends Controller {
  static targets = [
    "stepContent",
    "stepItem",
    "checkIcon",
    "unCheckIcon",
    "stepSpan",
    "initialTab",
    "checkContactIcon",
    "unCheckContactIcon",
    "checkIntegrationIcon",
    "unCheckIntegrationIcon",
    "contactTab",
    "integrationTab",
    "integrationSpan",
    "contactSpan",
    "backButton",
    "nextButton",
    "submitButton",
  ]

  connect() {
    this.currentIndex = 0
    this._onResize = this.updateMobileStepperVisibility.bind(this)

    this.element.addEventListener("input", this.updateNextButton.bind(this))
    this.element.addEventListener("change", this.updateNextButton.bind(this))

    const initialTab = this.initialTabTarget?.dataset?.tabValue

    if (initialTab === "integrations") {
      this.setInitialStep({
        index: this.getIndexByStep("siem"),
        parents: [
          this._parent(
            this.contactTabTarget,
            this.contactSpanTarget,
            this.checkContactIconTarget,
            this.unCheckContactIconTarget,
          ),
        ],
      })
    } else if (initialTab === "submit") {
      this.setInitialStep({
        index: this.getIndexByStep("submit"),
        parents: [
          this._parent(
            this.contactTabTarget,
            this.contactSpanTarget,
            this.checkContactIconTarget,
            this.unCheckContactIconTarget,
          ),
          this._parent(
            this.integrationTabTarget,
            this.integrationSpanTarget,
            this.checkIntegrationIconTarget,
            this.unCheckIntegrationIconTarget,
          ),
        ],
        lastSpanSpecial: true,
      })

      this.toggleButton(this.submitButtonTarget, false)
    }

    this.showCurrentStep()
    window.addEventListener("resize", this._onResize)
  }

  disconnect() {
    window.removeEventListener("resize", this._onResize)
    this._onResize = null
  }

  setInitialStep({ index = 0, parents = [], lastSpanSpecial = false } = {}) {
    if (typeof index !== "number" || index < 0) index = 0

    this.currentIndex = index
    this.markRangeComplete(0, index)

    parents.forEach(({ tab, span, check, uncheck }) => {
      if (tab || span || check || uncheck)
        this.markParent(tab, span, check, uncheck)
    })

    if (lastSpanSpecial && this.stepSpanTargets[index]) {
      const span = this.stepSpanTargets[index]
      this.toggleSpanStyles(span, true)
      span.classList.add("!bg-gray-500", "dark:!bg-gray-500")
      span.classList.remove("bg-gray-50", "dark:bg-gray-50")
    }
  }

  _parent(tab, span, check, uncheck) {
    return { tab, span, check, uncheck }
  }

  submit() {
    const stepSpan = this.stepSpanTargets[this.currentIndex]
    if (!stepSpan) return
    this.toggleSpanStyles(stepSpan, true)
    stepSpan.classList.add("bg-gray-500", "dark:!bg-gray-500")
    stepSpan.classList.remove("bg-gray-50", "dark:bg-gray-50")
  }

  returnToStart() {
    this.resetAll()
    this.currentIndex = 0
    this.showCurrentStep()
    this.toggleButton(this.submitButtonTarget, true)
  }

  resetAll() {
    this.stepContentTargets.forEach((el, i) =>
      el.classList.toggle("hidden", i !== 0),
    )

    this.stepItemTargets.forEach((item, i) => {
      const span = this.stepSpanTargets[i]
      if (item.dataset.step === "submit" && span) {
        span.classList.remove("!bg-gray-500", "dark:!bg-gray-500")
        span.classList.add("bg-gray-50", "dark:bg-gray-50")
      }
      this.toggleIcons(
        this.checkIconTargets[i],
        this.unCheckIconTargets[i],
        false,
      )
      if (span) this.toggleSpanStyles(span, false)
      this.toggleLineStyles(item, false)
    })

    this.unmarkParent(
      this.contactTabTarget,
      this.contactSpanTarget,
      this.checkContactIconTarget,
      this.unCheckContactIconTarget,
    )
    this.unmarkParent(
      this.integrationTabTarget,
      this.integrationSpanTarget,
      this.checkIntegrationIconTarget,
      this.unCheckIntegrationIconTarget,
    )
  }

  next() {
    if (this.currentIndex < this.stepContentTargets.length - 1) {
      this.updateStepState(this.currentIndex, true)
      this.currentIndex++
      this.showCurrentStep()
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.updateStepState(this.currentIndex - 1, false)
      this.currentIndex--
      this.showCurrentStep()
    }
  }

  skip() {
    this.next()
  }

  toggleButton(button, enabled) {
    if (!button) return
    button.disabled = !enabled
    button.classList.toggle("opacity-50", !enabled)
    button.classList.toggle("cursor-not-allowed", !enabled)
  }

  currentNextButton() {
    return this.nextButtonTargets[this.currentIndex] || null
  }

  updateNextButton() {
    const step = this.stepContentTargets[this.currentIndex]
    const button = this.currentNextButton()
    if (!step || !button) return

    const stepName = step.dataset?.step

    let inputs

    if (
      ["project_manager", "siem_admin", "firewall_admin"].includes(stepName)
    ) {
      inputs = step.querySelectorAll(
        'input[name*="[name]"], input[name*="[email]"], input[name*="[phone]"]',
      )
    } else {
      inputs = step.querySelectorAll(
        'input:not([type="hidden"]), textarea, select, input[type="hidden"]',
      )
    }

    const hasAnyValue = Array.from(inputs).some(
      (input) => input.value && input.value.trim().length > 0,
    )

    this.toggleButton(button, hasAnyValue)
  }

  showCurrentStep() {
    this.stepContentTargets.forEach((el, i) =>
      el.classList.toggle("hidden", i !== this.currentIndex),
    )

    this.stepItemTargets.forEach((item, i) => {
      const isActive = i === this.currentIndex
      item.classList.toggle("text-gray-100", isActive)
      item.classList.toggle("dark:text-gray-100", isActive)
      item.classList.toggle("text-gray-opaque-600", !isActive)
      item.classList.toggle("dark:text-gray-opaque-600", !isActive)
    })

    this.toggleButton(this.backButtonTarget, this.currentIndex > 0)

    this.updateNextButton()

    this.updateMobileStepperVisibility()
  }

  markRangeComplete(start, end) {
    const s = Math.max(0, start)
    const e = Math.min(this.stepItemTargets.length, end)
    for (let i = s; i < e; i++) this.updateStepState(i, true)
  }

  updateStepState(index, isComplete) {
    const stepItem = this.stepItemTargets[index]
    const stepSpan = this.stepSpanTargets[index]
    if (!stepItem || !stepSpan) return

    const stepName = stepItem.dataset.step
    if (stepName === "firewall_admin") {
      // Mark only the Contact tab as complete when reaching "firewall_admin"
      this.toggleParent(
        this.contactTabTarget,
        this.contactSpanTarget,
        this.checkContactIconTarget,
        this.unCheckContactIconTarget,
        isComplete,
      )
      this.toggleSpanStyles(this.integrationSpanTarget, isComplete)
      this.toggleLineStyles(this.integrationTabTarget, isComplete)
    } else if (stepName === "other") {
      // Mark both Contact and Integration tabs as complete on the "other" step
      this.toggleParent(
        this.contactTabTarget,
        this.contactSpanTarget,
        this.checkContactIconTarget,
        this.unCheckContactIconTarget,
        isComplete,
      )
      this.toggleParent(
        this.integrationTabTarget,
        this.integrationSpanTarget,
        this.checkIntegrationIconTarget,
        this.unCheckIntegrationIconTarget,
        isComplete,
      )
    }

    this.toggleIcons(
      this.checkIconTargets[index],
      this.unCheckIconTargets[index],
      isComplete,
    )
    this.toggleSpanStyles(stepSpan, isComplete)
    this.toggleLineStyles(stepItem, isComplete)
  }

  toggleParent(item, span, check, uncheck, isComplete) {
    if (isComplete) this.markParent(item, span, check, uncheck)
    else this.unmarkParent(item, span, check, uncheck)
  }

  markParent(item, span, check, uncheck) {
    if (check || uncheck) this.toggleIcons(check, uncheck, true)
    if (span) this.toggleSpanStyles(span, true)
    if (item) this.toggleLineStyles(item, true)
  }

  unmarkParent(item, span, check, uncheck) {
    if (check || uncheck) this.toggleIcons(check, uncheck, false)
    if (span) this.toggleSpanStyles(span, false)
    if (item) this.toggleLineStyles(item, false)
  }

  toggleIcons(check, uncheck, isComplete) {
    check?.classList?.toggle("hidden", !isComplete)
    uncheck?.classList?.toggle("hidden", isComplete)
  }

  toggleSpanStyles(span, isComplete) {
    if (!span) return

    const remove = isComplete
      ? [
          "border-black-400",
          "dark:border-black-400",
          "text-black-400",
          "dark:text-black-400",
        ]
      : [
          "border-gray-100",
          "dark:border-gray-100",
          "text-gray-100",
          "dark:text-gray-100",
        ]

    const add = isComplete
      ? [
          "border-gray-100",
          "dark:border-gray-100",
          "text-gray-100",
          "dark:text-gray-100",
        ]
      : [
          "border-black-400",
          "dark:border-black-400",
          "text-black-400",
          "dark:text-black-400",
        ]

    span.classList.remove(...remove)
    span.classList.add(...add)
  }

  toggleLineStyles(item, isComplete) {
    if (!item) return
    item.classList.toggle("after:border-solid", isComplete)
    item.classList.toggle("after:border-primary-100", isComplete)
    item.classList.toggle("after:border-dashed", !isComplete)
    item.classList.toggle("after:border-black-400", !isComplete)
  }

  getIndexByStep(stepName) {
    return this.stepItemTargets.findIndex(
      (item) => item.dataset.step === stepName,
    )
  }

  updateMobileStepperVisibility() {
    const index = this.currentIndex
    const steps = this.stepItemTargets
    const contact = this.contactTabTarget
    const integration = this.integrationTabTarget

    // Only operate mobile logic under breakpoint
    const isMobile = window.matchMedia(
      `(max-width: ${MOBILE_BREAKPOINT}px)`,
    ).matches

    if (!isMobile) {
      steps.forEach((step) => step.classList.remove("hidden"))
      this.removeMobileAfterStyle()
      return
    }

    const stepOrder = steps.map((s) => s.dataset.step)
    const current = stepOrder[index]

    const visibleMap = {
      project_manager: ["team", "project_manager", "siem_admin"],
      siem_admin: ["project_manager", "siem_admin", "firewall_admin"],
      firewall_admin: ["siem_admin", "firewall_admin", "integrations"],
      integrations: ["firewall_admin", "integrations", "siem"],
      siem: ["integrations", "siem", "firewall"],
      firewall: ["siem", "firewall", "other"],
      other: ["firewall", "other", "submit"],
      submit: ["other", "submit"],
    }

    const visible = visibleMap[current] || []

    if (contact) contact.classList.add("hidden")
    if (integration) integration.classList.add("hidden")

    steps.forEach((step) => step.classList.add("hidden"))

    visible.forEach((name) => {
      let el = steps.find((s) => s.dataset.step === name)
      if (name === "team") el = contact
      if (name === "integrations") el = integration
      if (el) el.classList.remove("hidden")
    })

    this.hideThirdAfterLine(visible, isMobile)
  }

  hideThirdAfterLine(visible, isMobile) {
    this.removeMobileAfterStyle()
    if (!isMobile) return
    if (!visible || visible.length !== 3) return
    const third = visible[2]
    if (!third) return
    const css = `
      #stepper [data-step="${third}"]::after {
        content: none !important;
        display: none !important;
        border: none !important;
      }
    `
    const style = document.createElement("style")
    style.id = "stepper-after-hide"
    style.textContent = css
    document.head.appendChild(style)
  }

  removeMobileAfterStyle() {
    const el = document.getElementById("stepper-after-hide")
    if (el) el.remove()
  }
}
